import { motion } from 'framer-motion';
import { Search, BookOpen, Clock, ChevronRight, ArrowLeft, Target, CheckCircle2, Calendar, MapPin, Mail, Phone, GraduationCap } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

export const CatalogueTab = ({
  showDetail, selectedTraining, catalogData, filteredCatalog,
  categories, levels, selectedCategory, setSelectedCategory,
  selectedLevel, setSelectedLevel, searchQuery, setSearchQuery,
  handleViewDetail, handleBackToCatalog, handleOpenRegistration, handleOpenInfoRequest
}) => (
  <>
    {/* CATALOGUE LIST */}
    {!showDetail && (
      <motion.section
        key="catalogue"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        className="py-16 bg-white"
        data-testid="catalog-trainings"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="text-center mb-10">
            <span className="inline-flex items-center gap-2 bg-teal-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-4">
              <BookOpen className="w-3.5 h-3.5" />
              Catalogue de formations
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-3">Nos Formations Professionnelles</h2>
            <p className="text-slate-500 max-w-2xl mx-auto">
              Découvrez notre catalogue complet de formations pour développer vos compétences.
              {' '}{catalogData.length} formations dans {categories.length - 1} domaines.
            </p>
          </div>

          {/* Search bar */}
          <div className="bg-white border border-slate-200 rounded-xl p-4 mb-8">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1 relative w-full">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input placeholder="Rechercher une formation..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 border-slate-200 h-11" data-testid="search-input" />
              </div>
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full md:w-52 h-11 border-slate-200" data-testid="category-select"><SelectValue placeholder="Toutes catégories" /></SelectTrigger>
                <SelectContent>{categories.map(cat => (<SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>))}</SelectContent>
              </Select>
              <Select value={selectedLevel} onValueChange={setSelectedLevel}>
                <SelectTrigger className="w-full md:w-40 h-11 border-slate-200" data-testid="level-select"><SelectValue placeholder="Tous niveaux" /></SelectTrigger>
                <SelectContent>{levels.map(level => (<SelectItem key={level} value={level}>{level}</SelectItem>))}</SelectContent>
              </Select>
            </div>
          </div>

          {/* Category cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            {categories.filter(c => c.id !== 'all').map(cat => (
              <div key={cat.id} onClick={() => setSelectedCategory(selectedCategory === cat.id ? 'all' : cat.id)}
                className={`bg-white border rounded-xl p-5 cursor-pointer transition-all hover:shadow-md ${selectedCategory === cat.id ? 'border-teal-500 shadow-sm' : 'border-slate-200'}`}
                data-testid={`category-card-${cat.id}`}
              >
                {cat.icon && (
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${selectedCategory === cat.id ? 'bg-teal-50' : 'bg-slate-50'}`}>
                    <cat.icon className={`w-5 h-5 ${selectedCategory === cat.id ? 'text-teal-600' : 'text-slate-500'}`} />
                  </div>
                )}
                <h4 className={`font-semibold text-sm mb-1 ${selectedCategory === cat.id ? 'text-teal-700' : 'text-slate-800'}`}>{cat.name}</h4>
                <p className="text-xs text-slate-400">{cat.count} formations</p>
              </div>
            ))}
          </div>

          <p className="text-teal-700 text-sm font-medium mb-6" data-testid="results-count">{filteredCatalog.length} formations trouvées</p>

          {/* Training grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCatalog.map((training) => (
              <motion.div key={training.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-slate-200 rounded-xl overflow-hidden hover:border-teal-400 hover:shadow-lg transition-all cursor-pointer group"
                onClick={() => handleViewDetail(training)} data-testid={`training-card-${training.id}`}
              >
                <div className="relative h-44 overflow-hidden">
                  <img src={training.image} alt={training.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  <div className="absolute top-3 left-3"><span className="inline-block bg-teal-600 text-white text-xs font-semibold px-3 py-1 rounded-full">{training.category}</span></div>
                  <div className="absolute top-3 right-3"><span className="inline-block bg-white text-slate-700 text-xs font-medium px-3 py-1 rounded-full shadow-sm">{training.modality}</span></div>
                </div>
                <div className="p-5">
                  <h3 className="text-base font-bold text-slate-900 mb-2 group-hover:text-teal-700 transition-colors">{training.title}</h3>
                  <p className="text-slate-500 text-sm mb-4 line-clamp-2">{training.description}</p>
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <span className="flex items-center gap-1 text-slate-400"><Clock className="w-4 h-4" />{training.duration}</span>
                      <span className="inline-block border border-slate-200 text-slate-600 text-xs font-medium px-2.5 py-0.5 rounded-full">{training.level}</span>
                    </div>
                    <ChevronRight className="w-5 h-5 text-teal-500 group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>
    )}

    {/* DETAIL VIEW */}
    {showDetail && selectedTraining && (
      <motion.section key="detail" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className="py-12 bg-slate-50" data-testid="training-detail">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <Button variant="ghost" onClick={handleBackToCatalog} className="mb-6 text-slate-600 hover:text-teal-700">
            <ArrowLeft className="w-4 h-4 mr-2" />Retour au catalogue
          </Button>
          <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-6">
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <div className="flex gap-3 mb-4">
                  <Badge className="bg-teal-600 text-white">{selectedTraining.category}</Badge>
                  <Badge variant="secondary">{selectedTraining.modality}</Badge>
                  <Badge variant="outline">{selectedTraining.level}</Badge>
                </div>
                <h1 className="text-3xl font-bold text-slate-900 mb-4">{selectedTraining.title}</h1>
                <p className="text-lg text-slate-600 mb-6">{selectedTraining.description}</p>
                <div className="flex flex-wrap gap-6 text-sm text-slate-600 border-t pt-6">
                  <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-teal-700" /><span>Durée : <strong>{selectedTraining.duration}</strong></span></div>
                  <div className="flex items-center gap-2"><Calendar className="w-5 h-5 text-teal-700" /><span>{selectedTraining.sessions}</span></div>
                  <div className="flex items-center gap-2"><MapPin className="w-5 h-5 text-teal-700" /><span>{selectedTraining.modality}</span></div>
                </div>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2"><Target className="w-5 h-5 text-teal-700" />Objectifs de la formation</h2>
                <ul className="space-y-3">{selectedTraining.objectives.map((obj, i) => (
                  <li key={i} className="flex items-start gap-3"><CheckCircle2 className="w-5 h-5 text-teal-600 flex-shrink-0 mt-0.5" /><span className="text-slate-600">{obj}</span></li>
                ))}</ul>
              </div>
              <div className="bg-white rounded-2xl shadow-lg p-8">
                <h2 className="text-xl font-bold text-slate-900 mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-teal-700" />Programme pédagogique</h2>
                <ol className="space-y-3">{selectedTraining.program.map((item, i) => (
                  <li key={i} className="flex items-start gap-4">
                    <span className="w-7 h-7 rounded-full bg-teal-100 text-teal-700 flex items-center justify-center text-sm font-semibold flex-shrink-0">{i + 1}</span>
                    <span className="text-slate-600 pt-1">{item}</span>
                  </li>
                ))}</ol>
              </div>
            </div>
            <div className="lg:col-span-1">
              <div className="bg-white rounded-2xl shadow-lg p-6 sticky top-24">
                <h3 className="text-xl font-bold text-slate-900 mb-6">S'inscrire à cette formation</h3>
                <div className="space-y-4 mb-6 text-sm">
                  <div className="flex justify-between py-2 border-b"><span className="text-slate-500">Durée</span><span className="font-medium">{selectedTraining.duration}</span></div>
                  <div className="flex justify-between py-2 border-b"><span className="text-slate-500">Sessions</span><span className="font-medium">{selectedTraining.sessions}</span></div>
                  <div className="flex justify-between py-2 border-b"><span className="text-slate-500">Modalité</span><span className="font-medium">{selectedTraining.type}</span></div>
                  <div className="flex justify-between py-2 border-b"><span className="text-slate-500">Niveau</span><Badge variant="outline">{selectedTraining.level}</Badge></div>
                </div>
                <div className="space-y-3">
                  <Button onClick={() => handleOpenRegistration(selectedTraining)} className="w-full bg-teal-700 hover:bg-teal-800 text-white rounded-xl py-6" data-testid="inscription-btn">
                    <GraduationCap className="w-5 h-5 mr-2" />S'inscrire à cette formation
                  </Button>
                  <Button variant="outline" onClick={() => handleOpenInfoRequest(selectedTraining)} className="w-full rounded-xl py-6" data-testid="info-btn">
                    <Mail className="w-5 h-5 mr-2" />Demander des informations
                  </Button>
                  <Button variant="ghost" asChild className="w-full rounded-xl py-6">
                    <a href="tel:+68987737815"><Phone className="w-5 h-5 mr-2" />Appeler : +689 87 73 78 15</a>
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </motion.section>
    )}
  </>
);
